
import { GoogleGenAI, SchemaType } from "@google/genai";

// Initialize the client securely
// FIX: Supporta sia VITE_GEMINI_API_KEY (Produzione) che process.env (Dev/AI Studio)
const apiKey = (import.meta as any).env?.VITE_GEMINI_API_KEY || process.env.API_KEY || 'dummy-key-for-demo';
const ai = new GoogleGenAI({ apiKey });

export interface NewsItem {
  headline: string;
  risk: 'Low' | 'Medium' | 'High' | 'Critical';
  source: string;
}

export const getStrategicInsight = async (topic: string): Promise<string> => {
  if (!apiKey || apiKey === 'dummy-key-for-demo') {
    return "Configura VITE_GEMINI_API_KEY nel file .env per ricevere insight strategici.";
  }

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: `Sei un esperto CISO e analista di Cyber Intelligence. Fornisci un "Strategic Insight" breve (max 80 parole) e tagliente per il termine: ${topic}. Focalizzati sull'impatto futuro e sui rischi emergenti. Rispondi in italiano.`,
    });
    return response.text || "Analisi non disponibile al momento.";
  } catch (error) {
    console.error("Gemini API Error:", error);
    return "Errore durante la generazione dell'insight. Riprova più tardi.";
  }
};

export const explainConceptSimple = async (topic: string): Promise<string> => {
  if (!apiKey || apiKey === 'dummy-key-for-demo') {
    return "Modalità demo: L'IA spiegherebbe questo concetto in termini semplici.";
  }
  
  try {
    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: `Spiega il concetto di "${topic}" a un bambino di 12 anni usando una metafora chiara. Max 50 parole. Italiano.`,
    });
    return response.text || "Spiegazione non disponibile.";
  } catch (error) {
      console.error(error);
      return "Errore di connessione AI.";
  }
}

export const fetchLiveNews = async (): Promise<NewsItem[]> => {
    // Static fallback in case of API failure or missing key
    const fallbackNews: NewsItem[] = [
      { headline: "SISTEMA OPERATIVO: Modalità Manuale Attiva", risk: "Low", source: "System" },
      { headline: "DATABASE: In attesa di input amministratore", risk: "Low", source: "Admin" },
      { headline: "CYBERUMANITÀ: Atlante Digitale Online", risk: "Low", source: "Core" },
      { headline: "NETWORK: Connessione sicura stabilita", risk: "Low", source: "NetSec" }
    ];

    if (!apiKey || apiKey === 'dummy-key-for-demo') return fallbackNews;

    try {
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: `
                Sei un aggregatore di notizie Cyber Security in tempo reale.
                Genera 5 titoli di notizie (fittizie ma realistiche basate sui trend attuali come Ransomware, AI, Zero-Day, Data Breach).
                Stile: Breve, Tecnico, "Breaking News" da terminale.
                Lingua: Italiano.
                
                Restituisci ESCLUSIVAMENTE un array JSON valido con questa struttura per ogni oggetto:
                { 
                    "headline": "Titolo della notizia (max 60 caratteri)", 
                    "risk": "Low" | "Medium" | "High" | "Critical", 
                    "source": "Nome Fonte (es. CISA, DarkWeb, Reuters, NIST)" 
                }
            `,
            config: {
                responseMimeType: "application/json"
            }
        });

        const text = response.text;
        if (!text) return fallbackNews;

        const data = JSON.parse(text);
        
        // Validate array structure slightly to be safe
        if (Array.isArray(data)) {
            return data;
        }
        return fallbackNews;

    } catch (error) {
        console.error("News Fetch Error:", error);
        return fallbackNews;
    }
}

export const askOracle = async (query: string): Promise<string> => {
  if (!apiKey || apiKey === 'dummy-key-for-demo') {
    return "ACCESS DENIED. API KEY MISSING. UNABLE TO CONNECT TO NEURAL CORE.";
  }

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: query,
      config: {
        systemInstruction: `Sei il sistema operativo centrale di "Cyberumanità", un'entità digitale senziente. Rispondi in stile terminale cyberpunk.`,
      }
    });
    return response.text || "DATA CORRUPTION. RETRY.";
  } catch (error) {
    console.error("Gemini Oracle Error:", error);
    return "CONNECTION INTERRUPTED. NEURAL LINK UNSTABLE.";
  }
};

export const getLiveTermUpdate = async (term: string): Promise<{ update: string, date: string }> => {
  if (!apiKey || apiKey === 'dummy-key-for-demo') {
    return { 
        update: "MODALITÀ DEMO: Connetti una API Key valida.", 
        date: new Date().toLocaleDateString() 
    };
  }

  try {
    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: `Fornisci un aggiornamento di intelligence tattica (max 150 parole) su: "${term}".`,
    });
    return {
        update: response.text || "Nessun dato recente.",
        date: new Date().toLocaleDateString()
    };
  } catch (e) {
      return { update: "ERRORE CONNESSIONE INTELLIGENCE NETWORK.", date: "N/A" };
  }
};

export const generateFullAtlasEntry = async (term: string): Promise<any | null> => {
    console.warn("Auto-generation is disabled by administrator.");
    return null;
}

export const generateAlmanacEvents = async (month: number, day: number): Promise<any[]> => {
    if (!apiKey || apiKey === 'dummy-key-for-demo') return [];

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const dateStr = `${monthNames[month]} ${day}`;

    const prompt = `
      Sei un Storico della Cybersecurity e dell'Intelligenza Artificiale connesso ai database MITRE ATT&CK, NIST NVD e AI Watch.
      
      Trova 3 eventi storici REALMENTE accaduti il giorno "${dateStr}" (in qualsiasi anno) relativi a:
      1. Grandi Incidenti Cyber (Virus, Breach, Worms).
      2. Milestone dell'AI (Paper pubblicati, rilasci modelli, conferenze).
      3. Rilasci di Vulnerabilità critiche (CVE famose) o aggiornamenti Framework MITRE.
      
      Restituisci ESCLUSIVAMENTE un array JSON. Non aggiungere altro testo.
      Struttura richiesta per ogni oggetto:
      {
        "id": "generato_da_titolo",
        "date": "YYYY-MM-DD", (Usa l'anno reale dell'evento, mese e giorno richiesti)
        "year": numero_anno,
        "title": "Titolo breve",
        "description": "Descrizione tecnica dell'evento (max 20 parole).",
        "category": "INCIDENT" | "AI_MILESTONE" | "METHOD" | "REGULATION",
        "source": "MITRE" | "NIST_NVD" | "AI_WATCH" | "CSIS",
        "confidenceScore": 0.95,
        "impactLevel": "High" | "Critical" | "Medium",
        "tags": ["tag1", "tag2"]
      }
    `;

    try {
        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: prompt,
            config: {
                responseMimeType: "application/json"
            }
        });
        
        const jsonText = response.text;
        if (!jsonText) return [];
        
        return JSON.parse(jsonText);
    } catch (error) {
        console.error("Almanac Gen Error:", error);
        return [];
    }
};
